import _ from 'lodash';
import React from 'react';
import type { BreadcrumbsProps } from '../components/Breadcrumbs';
import Breadcrumbs from '../components/Breadcrumbs';
import topicPath, { publicTopicPath } from '../utils/url/topicPath';
import useIsPublicView from '../utils/useIsPublicView';
import * as styles from './TopicBreadcrumbs.module.scss';
import TopicNameWithIcon from './TopicNameWithIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const TOPICS_FRAGMENT = gql(`
  fragment TopicBreadcrumbs_topics on PublicTopicFields {
    name
    ...topicPath_topic
    ...TopicNameWithIcon_topic
  }
`);

interface TopicBreadcrumbsProps {
  link?: boolean;
  names?: React.ReactNode[];
  showRoot?: boolean;
  showIcon?: boolean;
  size?: BreadcrumbsProps['size'];
  tab?: 'subtopics';
  topics: DocumentType<typeof TOPICS_FRAGMENT>[];
  truncate?: BreadcrumbsProps['truncate'];
  innerRef?: React.Ref<HTMLOListElement>;
  maximumAllowedSpace?: number;
}

const TopicBreadcrumbs = ({
  link = false,
  names = [],
  showRoot = false,
  showIcon = false,
  size,
  tab = undefined,
  topics,
  truncate,
  innerRef,
  maximumAllowedSpace,
}: TopicBreadcrumbsProps) => {
  const isPublicView = useIsPublicView();
  const items = _.compact(
    _.zip(topics, names)
      .toReversed()
      .map(([topic, name]) => {
        if (!topic) return null;

        const finalName = name ?? topic.name;
        const item: BreadcrumbsProps['items'][number] = {
          name: showIcon ? (
            <TopicNameWithIcon name={finalName} topic={topic} />
          ) : (
            finalName
          ),
        };

        if (showIcon) {
          item.name = (
            <TopicNameWithIcon name={name ?? topic.name} topic={topic} />
          );
        }

        if (link) {
          item.to = isPublicView
            ? publicTopicPath(topic)
            : topicPath(topic, tab);
        }

        return item;
      }),
  );

  if (showRoot) {
    items.unshift({ name: 'Content Map', to: link ? '/topics' : undefined });
  }

  return (
    <div className={styles.container}>
      <Breadcrumbs
        ref={innerRef}
        items={items}
        size={size}
        truncate={truncate}
        maximumAllowedSpace={maximumAllowedSpace}
      />
    </div>
  );
};

export default TopicBreadcrumbs;
